import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentUser } from "../utils/auth";
import { getAllSeries, addSeries } from "../utils/seriesUtils";
import { isTitleDuplicate, hasSynopsisTooSimilar } from "../utils/plagiarism";
import "../styles/styles.css";

const Upload = () => {
  const navigate = useNavigate();
  const user = getCurrentUser(); // { email, role } o null

  // Si no hay sesión o no es creador, redirigir
  useEffect(() => {
    if (!user || user.role !== "creator") {
      navigate("/login");
    }
  }, [user, navigate]);

  // Estados del formulario
  const [title, setTitle] = useState("");
  const [synopsis, setSynopsis] = useState("");
  const [episodes, setEpisodes] = useState("");
  const [duration, setDuration] = useState("");
  const [genres, setGenres] = useState([]);
  const [releaseDate, setReleaseDate] = useState("");
  const [thumbnail, setThumbnail] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const allGenres = [
    "Acción",
    "Drama",
    "Comedia",
    "Ciencia Ficción",
    "Romance",
    "Suspense",
    "Terror",
  ];

  const handleGenreChange = (e) => {
    const { value, checked } = e.target;
    if (checked) {
      setGenres([...genres, value]);
    } else {
      setGenres(genres.filter((g) => g !== value));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    // Validaciones básicas
    if (
      !title.trim() ||
      !synopsis.trim() ||
      !episodes ||
      !duration.trim() ||
      genres.length === 0 ||
      !thumbnail.trim() ||
      !videoUrl.trim()
    ) {
      setError("Por favor, completa todos los campos obligatorios.");
      return;
    }

    // Título duplicado
    if (isTitleDuplicate(title)) {
      setError("Ya existe una serie con este título.");
      return;
    }

    // Plagio de sinopsis
    const { suspicious, existingTitle, ratio } = hasSynopsisTooSimilar(
      synopsis,
      0.5
    );
    if (suspicious) {
      setError(
        `La sinopsis es muy parecida a "${existingTitle}" (≈${(
          ratio * 100
        ).toFixed(0)}%). Modifícala.`
      );
      return;
    }

    // Construir objeto de la nueva serie
    const newSeries = {
      id: Date.now().toString(),
      title: title.trim(),
      synopsis: synopsis.trim(),
      episodes: parseInt(episodes, 10),
      durationPerEpisode: duration.trim(),
      genre: genres,
      releaseDate: releaseDate || new Date().toISOString().split("T")[0],
      popularity: 0,
      thumbnail: thumbnail.trim(),
      videoUrl: videoUrl.trim(),
      uploadedBy: user.email,
    };

    // Guardar y limpiar formulario
    addSeries(newSeries);
    setSuccessMsg("¡Contenido subido correctamente!");
    setTitle("");
    setSynopsis("");
    setEpisodes("");
    setDuration("");
    setGenres([]);
    setReleaseDate("");
    setThumbnail("");
    setVideoUrl("");
  };

  return (
    <div className="upload-container">
      <h2>Subir Nueva Serie/Película</h2>
      {error && <p className="error-message">{error}</p>}
      {successMsg && <p className="success-message">{successMsg}</p>}
      <form onSubmit={handleSubmit} className="upload-form">
        <label htmlFor="title">Título*:</label>
        <input
          type="text"
          id="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />

        <label htmlFor="synopsis">Sinopsis*:</label>
        <textarea
          id="synopsis"
          rows="4"
          value={synopsis}
          onChange={(e) => setSynopsis(e.target.value)}
          required
        />

        <label htmlFor="episodes">Número de episodios*:</label>
        <input
          type="number"
          id="episodes"
          min="1"
          value={episodes}
          onChange={(e) => setEpisodes(e.target.value)}
          required
        />

        <label htmlFor="duration">Duración por episodio*:</label>
        <input
          type="text"
          id="duration"
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
          required
        />

        <fieldset className="genre-fieldset">
          <legend>Géneros*:</legend>
          <div className="genre-options">
            {allGenres.map((g) => (
              <label key={g} className="genre-label">
                <input
                  type="checkbox"
                  value={g}
                  checked={genres.includes(g)}
                  onChange={handleGenreChange}
                />
                {g}
              </label>
            ))}
          </div>
        </fieldset>

        <label htmlFor="releaseDate">Fecha de estreno (opcional):</label>
        <input
          type="date"
          id="releaseDate"
          value={releaseDate}
          onChange={(e) => setReleaseDate(e.target.value)}
        />

        <label htmlFor="thumbnail">URL de la miniatura*:</label>
        <input
          type="text"
          id="thumbnail"
          placeholder="https://..."
          value={thumbnail}
          onChange={(e) => setThumbnail(e.target.value)}
          required
        />

        <label htmlFor="videoUrl">URL del video*:</label>
        <input
          type="text"
          id="videoUrl"
          placeholder="https://..."
          value={videoUrl}
          onChange={(e) => setVideoUrl(e.target.value)}
          required
        />

        <button type="submit" className="btn-submit">
          Subir Contenido
        </button>
      </form>
    </div>
  );
};

export default Upload;
